import { deleteFridge, deleteSensor } from '@gozoki/api'
import { MenuItem, Paper, Select, Tab, Tabs, Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { PlusSquare } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import z from 'zod/v3'
import { api } from '~/lib/tuyau'
import { AuthorizedButtonLink, withRoutePermissions } from '~/utils/rbac'
import { useAuthorizedTabs, useFirstAuthorizedTab } from '~/utils/rbacTabs'
import { AuthorizedButton } from '../../../components/communication/AuthorizedButton'
import { authorizedTabProps } from '../../../components/communication/AuthorizedTab'
import { ConfirmResourceDeletionButton } from '../../../components/communication/ConfirmDeletionButton'
import Page from '../../../components/Page'
import ActiveAlertsDrawer from '../../../components/routes/sensors/ActiveAlertsDrawer'
import { AlertSettingsPanel } from '../../../components/routes/sensors/AlertSettingsPanel'
import FridgeGrid from '../../../components/routes/sensors/FridgeGrid'
import { fridgesQueryOptions } from '../../../components/routes/sensors/queries'
import {
  alertingSensorIdSet,
  filterFridgesInAlert,
  filterSensorsInAlert,
} from '../../../components/routes/sensors/sensorAlertFilter'
import SensorChart from '../../../components/routes/sensors/SensorChart'
import SensorGrid from '../../../components/routes/sensors/SensorGrid'
import SensorStatusCards from '../../../components/routes/sensors/SensorStatusCards'
import {
  FRIDGE_DELETE,
  FRIDGE_EDIT,
  SENSOR_DELETE,
  SENSOR_EDIT,
  SENSORS_PAGE_READ,
  SENSORS_TABS,
} from '../../../components/routes/sensors/tabs'
import type { SensorAlertCategory, SensorData } from '../../../components/routes/sensors/types'
import { StoresPicker } from '../../../utils/dashboard/storesPicker'
import { fonts } from '../../../utils/theme/fonts.theme'
import { useSensorAlerts } from '../../../utils/websockets/useSensorAlerts'

const tabs = ['sensors', 'fridges', 'settings'] as const
// Valeur sentinelle du sélecteur : « En alerte » (≠ tout id de boutique, positifs).
const ALERTING_STORE_VALUE = -1
// Valeur « Toutes les boutiques » injectée par StoresPicker via allowAllStores.
const ALL_STORES_VALUE = 0
// Référence stable pour éviter de recalculer storeSelectOptions à chaque rendu.
const SENSOR_EXTRA_OPTIONS = [{ label: 'En alerte', value: ALERTING_STORE_VALUE }]

type CategoryFilter = 'ALL' | SensorAlertCategory

function categoryOf(sensor: SensorData): SensorAlertCategory {
  if (sensor.type === 'ROOM') return 'AMBIANT'
  if (sensor.fridge?.type === 'COLD_ROOM') return 'CHAMBRE_FROIDE'
  return 'FRIGO'
}

export const Route = createFileRoute('/_authenticated/sensors/')({
  staticData: {
    permission: SENSORS_PAGE_READ,
  },
  component: SensorsPage,
  validateSearch: z.object({
    tab: z.enum(tabs).optional(),
    categoryFilter: z
      .enum(['ALL', 'AMBIANT', 'FRIGO', 'CHAMBRE_FROIDE'] as const)
      .optional()
      .default('ALL'),
  }),
})
withRoutePermissions(Route)

function SensorsPage() {
  const navigate = Route.useNavigate()
  const { tab = 'sensors', categoryFilter = 'ALL' } = Route.useSearch()

  const [currentStore, setCurrentStore] = useState<number>(ALERTING_STORE_VALUE)
  const isAlertMode = currentStore === ALERTING_STORE_VALUE
  const effectiveStoreId = isAlertMode || currentStore === ALL_STORES_VALUE ? null : currentStore
  const [selectedSensorIds, setSelectedSensorIds] = useState<number[]>([])
  const [selectedFridgeIds, setSelectedFridgeIds] = useState<number[]>([])
  const [alertsDrawerOpen, setAlertsDrawerOpen] = useState(false)

  useSensorAlerts()

  const storeQuery = effectiveStoreId === null ? {} : { store: effectiveStoreId }

  const { data: sensors, isLoading } = useQuery(
    api.sensors.index.queryOptions({ query: storeQuery }),
  )
  const { data: fridges, isLoading: isLoadingFridges } = useQuery(fridgesQueryOptions(storeQuery))
  const { data: activeAlerts } = useQuery(api.sensors.activeAlerts.queryOptions({}))

  const authorizedTabs = useAuthorizedTabs(SENSORS_TABS)
  const firstAuthorizedTab = useFirstAuthorizedTab(authorizedTabs)
  // Un onglet interdit reste affiché (désactivé + tooltip), mais on n'en montre jamais le contenu :
  // on retombe sur le premier onglet accessible.
  const activeTab = authorizedTabs.find((item) => item.value === tab)?.authorized
    ? tab
    : (firstAuthorizedTab ?? tab)

  const alertingSensorIds = useMemo(() => alertingSensorIdSet(activeAlerts), [activeAlerts])

  // En mode alerte : ne garder que les équipements en alerte moteur (toutes boutiques).
  const sensorsInScope = useMemo(() => {
    if (!sensors) return undefined
    return isAlertMode ? filterSensorsInAlert(sensors, alertingSensorIds) : sensors
  }, [sensors, isAlertMode, alertingSensorIds])

  const fridgesInScope = useMemo(() => {
    if (!fridges) return undefined
    if (!isAlertMode) return fridges
    // Ne pas filtrer contre un set de capteurs vide pendant que `sensors` charge :
    // La liste des frigos garde les données précédentes (keepPreviousData) mais pas celle des
    // capteurs, donc on resterait en chargement plutôt que d'afficher un faux
    // « Aucun réfrigérateur en alerte » avec des frigos périmés.
    if (!sensors) return undefined
    return filterFridgesInAlert(fridges, sensors, alertingSensorIds)
  }, [fridges, sensors, isAlertMode, alertingSensorIds])

  // Réinitialiser les sélections et la page courante au changement de boutique / de
  // mode : évite une cible de suppression ou une série de graphe héritées d'une autre
  // boutique (la grille MUI ne purge pas la sélection vers les nouvelles lignes).
  useEffect(() => {
    setSelectedSensorIds([])
    setSelectedFridgeIds([])
  }, [currentStore])

  // Get sensor IDs from selected fridges
  const sensorIdsFromFridges = useMemo(() => {
    if (selectedFridgeIds.length === 0 || !fridges || !sensors) {
      return []
    }

    // Get fridge references from selected fridge IDs
    const selectedFridgeReferences = new Set(
      fridges
        .filter((fridge) => selectedFridgeIds.includes(fridge.id))
        .map((fridge) => fridge.reference),
    )

    // Get sensor IDs that belong to selected fridges
    return sensors
      .filter((sensor) => sensor.fridge && selectedFridgeReferences.has(sensor.fridge.reference))
      .map((sensor) => sensor.id)
  }, [selectedFridgeIds, fridges, sensors])

  // Combine selected sensor IDs with sensors from selected fridges
  const allSelectedSensorIds = useMemo(() => {
    const combined = [...selectedSensorIds, ...sensorIdsFromFridges]
    // Remove duplicates
    return [...new Set(combined)]
  }, [selectedSensorIds, sensorIdsFromFridges])

  // Filtre catégorie appliqué par-dessus le filtre alerte.
  const filteredSensors = useMemo(() => {
    if (!sensorsInScope) return undefined
    if (categoryFilter === 'ALL') return sensorsInScope
    return sensorsInScope.filter((sensor) => categoryOf(sensor) === categoryFilter)
  }, [sensorsInScope, categoryFilter])

  const handleChange = (newTab: (typeof tabs)[number]) => {
    setSelectedSensorIds([])
    setSelectedFridgeIds([])
    navigate({ search: { tab: newTab, categoryFilter: 'ALL' } })
  }

  const handleCategoryFilterChange = (value: CategoryFilter) => {
    setSelectedSensorIds([])
    navigate({ search: (prev) => ({ ...prev, categoryFilter: value }) })
  }

  // Delete mutations
  const deleteSensorMut = useMutation({
    mutationFn: deleteSensor,
    meta: { invalidates: [api.sensors.index.queryKey()] },
    onSuccess: () => {
      setSelectedSensorIds([])
    },
  })

  const deleteFridgeMut = useMutation({
    mutationFn: deleteFridge,
    meta: { invalidates: [api.sensors.fridges.queryKey()] },
    onSuccess: () => {
      setSelectedFridgeIds([])
    },
  })

  return (
    <Page>
      <div style={{ display: 'flex', paddingBottom: 10, paddingLeft: 10 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Équipements de Réfrigération
        </Typography>
        {activeTab === 'sensors' && selectedSensorIds.length === 1 && (
          <>
            <ConfirmResourceDeletionButton
              permission={SENSOR_DELETE}
              disabled={deleteSensorMut.isPending}
              loading={deleteSensorMut.isPending}
              buttonText="Supprimer ce capteur"
              title="Supprimer ce capteur ?"
              description="Etes-vous sûrs de vouloir supprimer ce capteur ? Cette action est irréversible et supprimera toutes les données associées à ce capteur."
              handleResourceDeletion={() => deleteSensorMut.mutateAsync(selectedSensorIds[0])}
            />
            <AuthorizedButton
              permission={SENSOR_EDIT}
              variant="contained"
              sx={{ marginRight: '16px' }}
              onClick={() =>
                navigate({
                  search: { sensorId: selectedSensorIds[0] },
                  to: '/sensors/new',
                })
              }
            >
              Modifier ce capteur
            </AuthorizedButton>
          </>
        )}
        {activeTab === 'fridges' && selectedFridgeIds.length === 1 && (
          <>
            <ConfirmResourceDeletionButton
              permission={FRIDGE_DELETE}
              disabled={deleteFridgeMut.isPending}
              loading={deleteFridgeMut.isPending}
              buttonText="Supprimer ce réfrigérateur"
              title="Supprimer ce réfrigérateur ?"
              description="Etes-vous sûrs de vouloir supprimer ce réfrigérateur ? Cette action est irréversible et supprimera toutes les données associées à ce réfrigérateur."
              handleResourceDeletion={() => deleteFridgeMut.mutateAsync(selectedFridgeIds[0])}
            />
            <AuthorizedButton
              permission={FRIDGE_EDIT}
              variant="contained"
              sx={{ marginRight: '16px' }}
              onClick={() =>
                navigate({
                  search: { fridgeId: selectedFridgeIds[0] },
                  to: '/sensors/fridge/new',
                })
              }
            >
              Modifier ce réfrigérateur
            </AuthorizedButton>
          </>
        )}
        {activeTab === 'sensors' && (
          <AuthorizedButtonLink variant="contained" sx={{ marginRight: '16px' }} to="/sensors/new">
            <PlusSquare />
            Ajouter un capteur
          </AuthorizedButtonLink>
        )}
        {activeTab === 'fridges' && (
          <AuthorizedButtonLink
            variant="contained"
            sx={{ marginRight: '16px' }}
            to="/sensors/fridge/new"
          >
            <PlusSquare />
            Ajouter un réfrigérateur
          </AuthorizedButtonLink>
        )}
      </div>
      <div style={{ minWidth: 200 }}>
        <StoresPicker
          storeSwitch
          setUniqueStore={setCurrentStore}
          extraOptions={SENSOR_EXTRA_OPTIONS}
          onlyBoutiques
          allowAllStores
          defaultStore={ALERTING_STORE_VALUE}
        />
      </div>
      <SensorStatusCards
        sensors={sensors}
        activeAlertSensorIds={alertingSensorIds}
        onAlertsClick={() => setAlertsDrawerOpen(true)}
      />
      <ActiveAlertsDrawer open={alertsDrawerOpen} onClose={() => setAlertsDrawerOpen(false)} />
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => {
          // Un onglet interdit n'est pas `disabled` (ça couperait le tooltip) : on neutralise ici.
          const target = authorizedTabs.find((item) => item.value === newValue)
          if (target?.authorized) handleChange(target.value)
        }}
        sx={{ marginBottom: 1 }}
      >
        {authorizedTabs.map((item) => (
          <Tab key={item.value} {...authorizedTabProps(item)} />
        ))}
      </Tabs>
      {activeTab === 'settings' ? (
        <AlertSettingsPanel />
      ) : (
        <>
          <Paper
            sx={{
              marginTop: '16px',
            }}
          >
            {activeTab === 'sensors' && (
              <>
                <div style={{ padding: '12px 16px 0' }}>
                  <Select
                    size="small"
                    value={categoryFilter}
                    onChange={(e) => handleCategoryFilterChange(e.target.value as CategoryFilter)}
                    displayEmpty
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="ALL">Tous les équipements</MenuItem>
                    <MenuItem value="AMBIANT">Ambiant</MenuItem>
                    <MenuItem value="FRIGO">Frigo</MenuItem>
                    <MenuItem value="CHAMBRE_FROIDE">Chambre froide</MenuItem>
                  </Select>
                </div>
                {isAlertMode &&
                activeAlerts !== undefined &&
                filteredSensors &&
                filteredSensors.length === 0 ? (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    align="center"
                    sx={{ padding: 4 }}
                  >
                    Aucun capteur en alerte
                  </Typography>
                ) : (
                  <SensorGrid
                    data={filteredSensors}
                    loading={isLoading && !sensors}
                    onSelectionChange={setSelectedSensorIds}
                  />
                )}
              </>
            )}
            {activeTab === 'fridges' &&
              (isAlertMode &&
              activeAlerts !== undefined &&
              fridgesInScope &&
              fridgesInScope.length === 0 ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                  sx={{ padding: 4 }}
                >
                  Aucun réfrigérateur en alerte
                </Typography>
              ) : (
                <FridgeGrid
                  data={fridgesInScope}
                  loading={isLoadingFridges && !fridges}
                  onSelectionChange={setSelectedFridgeIds}
                />
              ))}
          </Paper>
          <SensorChart
            selectedSensorIds={allSelectedSensorIds}
            storeId={effectiveStoreId}
            sensors={sensors}
          />
          <div style={{ margin: 10 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {activeTab === 'sensors'
                ? 'Sélectionnez des capteurs dans le tableau pour afficher leur historique'
                : "Sélectionnez des réfrigérateurs dans le tableau pour afficher l'historique de leurs capteurs"}
            </Typography>
          </div>
        </>
      )}
    </Page>
  )
}
